import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
  structuredData?: object;
  canonicalUrl?: string;
  googleSiteVerification?: string;
}

const SITE_NAME = "Mariah Blue Photography";
const DEFAULT_DESCRIPTION =
  "Professional photography portfolio by Mariah Blue. Specializing in authentic, unscripted moments captured with intention and care.";
const DEFAULT_IMAGE = "/favicon.png";
const BASE_URL =
  typeof window !== "undefined"
    ? window.location.origin
    : "https://mariahblue.com";

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  siteName = SITE_NAME,
  structuredData,
  canonicalUrl,
  googleSiteVerification,
}) => {
  const location = useLocation();
  const fullUrl = url || `${BASE_URL}${location.pathname}`;
  const fullImageUrl = image.startsWith("http") ? image : `${BASE_URL}${image}`;
  const canonical = canonicalUrl || fullUrl;
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Update or create meta tags
    const updateMetaTag = (
      name: string,
      content: string,
      attribute: string = "name"
    ) => {
      let element = document.querySelector(
        `meta[${attribute}="${name}"]`
      ) as HTMLMetaElement;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Basic meta tags
    updateMetaTag("description", description);
    updateMetaTag("theme-color", "#000000");
    
    // Google Search Console verification
    // Use prop if provided, otherwise use environment variable
    const verificationCode =
      googleSiteVerification ||
      (typeof process !== "undefined" &&
        process.env.REACT_APP_GOOGLE_SITE_VERIFICATION);
    if (verificationCode) {
      updateMetaTag("google-site-verification", verificationCode);
    }

    // Open Graph tags
    updateMetaTag("og:title", fullTitle, "property");
    updateMetaTag("og:description", description, "property");
    updateMetaTag("og:image", fullImageUrl, "property");
    updateMetaTag("og:url", fullUrl, "property");
    updateMetaTag("og:type", type, "property");
    updateMetaTag("og:site_name", siteName, "property");

    // Twitter Card tags
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", fullTitle);
    updateMetaTag("twitter:description", description);
    updateMetaTag("twitter:image", fullImageUrl);

    // Canonical URL
    let canonicalLink = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonical);

    // Structured data (JSON-LD)
    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll(
      'script[type="application/ld+json"]'
    );
    existingScripts.forEach((script) => script.remove());

    if (structuredData) {
      // Handle both single objects and arrays
      const dataArray = Array.isArray(structuredData)
        ? structuredData
        : [structuredData];
      dataArray.forEach((data) => {
        const script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    // Update HTML lang attribute if needed (keeping "en" for now)
    document.documentElement.setAttribute("lang", "en");
  }, [
    fullTitle,
    description,
    fullImageUrl,
    fullUrl,
    type,
    siteName,
    canonical,
    structuredData,
  ]);

  return null;
};
